import { patchPageBehaviorState } from './behavior-state.js';
import { findFocusCardCandidates } from './focus-card-candidates.js';
import { pickBestFocusCandidate } from './score-focus-candidate.js';

export type TrackFocusIntersectionOptions = {
  rootMargin?: string;
  scrollThrottleMs?: number;
};

/**
 * IntersectionObserver on large product cards (phase 5.7).
 * Picks the card closest to viewport center / pointer — not just max ratio.
 */
export function trackFocusIntersection(
  options?: TrackFocusIntersectionOptions,
): () => void {
  if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
    return () => {};
  }

  const ratios = new Map<Element, number>();
  const candidates = findFocusCardCandidates();

  if (candidates.length === 0) {
    patchPageBehaviorState({ focusedProduct: undefined });
    return () => {};
  }

  const candidateByElement = new Map(
    candidates.map((candidate) => [candidate.element, candidate]),
  );

  const syncFocusedProduct = () => {
    const best = pickBestFocusCandidate(
      [...ratios.entries()].map(([element, ratio]) => ({ element, ratio })),
    );

    if (!best) {
      patchPageBehaviorState({ focusedProduct: undefined });
      return;
    }

    const candidate = candidateByElement.get(best.element);
    patchPageBehaviorState({
      focusedProduct: {
        productName: candidate?.productName,
        productUrl: candidate?.productUrl,
        visibilityRatio: Math.round(best.ratio * 100) / 100,
      },
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        ratios.set(entry.target, entry.intersectionRatio);
      }
      syncFocusedProduct();
    },
    {
      root: null,
      rootMargin: options?.rootMargin ?? '0px',
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
    },
  );

  for (const candidate of candidates) {
    ratios.set(candidate.element, 0);
    observer.observe(candidate.element);
  }

  const scrollThrottleMs = options?.scrollThrottleMs ?? 100;
  let lastScrollSyncAt = 0;
  const onScroll = () => {
    const now = Date.now();
    if (now - lastScrollSyncAt < scrollThrottleMs) return;
    lastScrollSyncAt = now;
    syncFocusedProduct();
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  let lastPointerSyncAt = 0;
  const onPointerMove = () => {
    const now = Date.now();
    if (now - lastPointerSyncAt < scrollThrottleMs) return;
    lastPointerSyncAt = now;
    syncFocusedProduct();
  };
  window.addEventListener('mousemove', onPointerMove, { passive: true });

  syncFocusedProduct();

  return () => {
    observer.disconnect();
    ratios.clear();
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
    window.removeEventListener('mousemove', onPointerMove);
    patchPageBehaviorState({ focusedProduct: undefined });
  };
}
